/**
 * @deprecated This permission service is deprecated.
 * Use the new server-authoritative permission system instead:
 *
 * import { usePermissions, ActionGuard, CanManageMembers } from '@/shared/auth'
 *
 * The new system:
 * - Fetches permissions from /api/v1/users/me/permissions (server-authoritative)
 * - Uses 5 canonical roles: owner, admin, manager, user, auditor
 * - Provides ActionGuard, OwnerOnly, AdminOnly, etc. components
 * - Has proper permission context with React hooks
 *
 * Migration: Replace usages of permissionService with usePermissions() hook
 * and use ActionGuard components for UI permission checks.
 *
 * @see /shared/auth/permission-context.tsx
 * @see /shared/auth/action-guard.tsx
 */

/**
 * Permission Service for Role-Based Access Control
 * Implements granular permissions based on user roles and resource ownership
 * @deprecated Use permission-context.tsx and action-guard.tsx instead
 */

export type UserRole = 'owner' | 'admin' | 'manager' | 'member' | 'auditor';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  organizationId: string;
}

export interface Resource {
  id: string;
  createdBy: string;
  organizationId: string;
  type: 'agent' | 'mission' | 'resource' | 'rule';
}

export interface Permission {
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'export';
  scope?: 'own' | 'all'; // own = only resources created by user, all = any resource
}

// Define role permissions
const rolePermissions: Record<UserRole, Permission[]> = {
  owner: [
    // Owner has full access to everything (same as admin, but is the organization owner)
    { resource: 'dashboard', action: 'read', scope: 'all' },
    { resource: 'dashboard', action: 'update', scope: 'all' },
    { resource: 'organization', action: 'create', scope: 'all' },
    { resource: 'organization', action: 'read', scope: 'all' },
    { resource: 'organization', action: 'update', scope: 'all' },
    { resource: 'organization', action: 'delete', scope: 'all' },
    { resource: 'members', action: 'create', scope: 'all' },
    { resource: 'members', action: 'read', scope: 'all' },
    { resource: 'members', action: 'update', scope: 'all' },
    { resource: 'members', action: 'delete', scope: 'all' },
    { resource: 'agents', action: 'create', scope: 'all' },
    { resource: 'agents', action: 'read', scope: 'all' },
    { resource: 'agents', action: 'update', scope: 'all' },
    { resource: 'agents', action: 'delete', scope: 'all' },
    { resource: 'missions', action: 'create', scope: 'all' },
    { resource: 'missions', action: 'read', scope: 'all' },
    { resource: 'missions', action: 'update', scope: 'all' },
    { resource: 'missions', action: 'delete', scope: 'all' },
    { resource: 'governance', action: 'create', scope: 'all' },
    { resource: 'governance', action: 'read', scope: 'all' },
    { resource: 'governance', action: 'update', scope: 'all' },
    { resource: 'governance', action: 'delete', scope: 'all' },
    { resource: 'resources', action: 'create', scope: 'all' },
    { resource: 'resources', action: 'read', scope: 'all' },
    { resource: 'resources', action: 'update', scope: 'all' },
    { resource: 'resources', action: 'delete', scope: 'all' },
    { resource: 'analytics', action: 'read', scope: 'all' },
    { resource: 'analytics', action: 'export', scope: 'all' },
    { resource: 'settings', action: 'read', scope: 'all' },
    { resource: 'settings', action: 'update', scope: 'all' },
    { resource: 'billing', action: 'read', scope: 'all' },
    { resource: 'billing', action: 'update', scope: 'all' },
    { resource: 'audit', action: 'read', scope: 'all' },
    { resource: 'audit', action: 'export', scope: 'all' },
  ],

  admin: [
    // Full access to everything
    { resource: 'dashboard', action: 'read', scope: 'all' },
    { resource: 'dashboard', action: 'update', scope: 'all' },
    { resource: 'organization', action: 'create', scope: 'all' },
    { resource: 'organization', action: 'read', scope: 'all' },
    { resource: 'organization', action: 'update', scope: 'all' },
    { resource: 'organization', action: 'delete', scope: 'all' },
    { resource: 'members', action: 'create', scope: 'all' },
    { resource: 'members', action: 'read', scope: 'all' },
    { resource: 'members', action: 'update', scope: 'all' },
    { resource: 'members', action: 'delete', scope: 'all' },
    { resource: 'agents', action: 'create', scope: 'all' },
    { resource: 'agents', action: 'read', scope: 'all' },
    { resource: 'agents', action: 'update', scope: 'all' },
    { resource: 'agents', action: 'delete', scope: 'all' },
    { resource: 'missions', action: 'create', scope: 'all' },
    { resource: 'missions', action: 'read', scope: 'all' },
    { resource: 'missions', action: 'update', scope: 'all' },
    { resource: 'missions', action: 'delete', scope: 'all' },
    { resource: 'governance', action: 'create', scope: 'all' },
    { resource: 'governance', action: 'read', scope: 'all' },
    { resource: 'governance', action: 'update', scope: 'all' },
    { resource: 'governance', action: 'delete', scope: 'all' },
    { resource: 'resources', action: 'create', scope: 'all' },
    { resource: 'resources', action: 'read', scope: 'all' },
    { resource: 'resources', action: 'update', scope: 'all' },
    { resource: 'resources', action: 'delete', scope: 'all' },
    { resource: 'analytics', action: 'read', scope: 'all' },
    { resource: 'analytics', action: 'export', scope: 'all' },
    { resource: 'settings', action: 'read', scope: 'all' },
    { resource: 'settings', action: 'update', scope: 'all' },
    { resource: 'billing', action: 'read', scope: 'all' },
    { resource: 'billing', action: 'update', scope: 'all' },
    { resource: 'audit', action: 'read', scope: 'all' },
    { resource: 'audit', action: 'export', scope: 'all' },
  ],

  manager: [
    // Can manage own resources, view all, invite members
    { resource: 'dashboard', action: 'read', scope: 'all' },
    { resource: 'dashboard', action: 'update', scope: 'own' },
    { resource: 'organization', action: 'read', scope: 'all' },
    { resource: 'members', action: 'create', scope: 'all' }, // Can invite
    { resource: 'members', action: 'read', scope: 'all' },
    { resource: 'agents', action: 'create', scope: 'own' },
    { resource: 'agents', action: 'read', scope: 'all' },
    { resource: 'agents', action: 'update', scope: 'own' }, // Only own agents
    { resource: 'agents', action: 'delete', scope: 'own' }, // Only own agents
    { resource: 'missions', action: 'create', scope: 'own' },
    { resource: 'missions', action: 'read', scope: 'all' },
    { resource: 'missions', action: 'update', scope: 'own' }, // Only own missions
    { resource: 'missions', action: 'delete', scope: 'own' }, // Only own missions
    { resource: 'governance', action: 'read', scope: 'all' },
    { resource: 'governance', action: 'create', scope: 'own' }, // Project-level rules
    { resource: 'governance', action: 'update', scope: 'own' },
    { resource: 'governance', action: 'delete', scope: 'own' },
    { resource: 'resources', action: 'create', scope: 'own' },
    { resource: 'resources', action: 'read', scope: 'all' },
    { resource: 'resources', action: 'update', scope: 'own' }, // Only own resources
    { resource: 'resources', action: 'delete', scope: 'own' }, // Only own resources
    { resource: 'analytics', action: 'read', scope: 'all' },
    { resource: 'analytics', action: 'export', scope: 'all' },
    { resource: 'settings', action: 'read', scope: 'all' },
    { resource: 'audit', action: 'read', scope: 'own' },
  ],

  member: [
    // Limited to own resources and viewing
    { resource: 'dashboard', action: 'read', scope: 'own' },
    { resource: 'organization', action: 'read', scope: 'all' },
    { resource: 'members', action: 'read', scope: 'all' },
    { resource: 'agents', action: 'read', scope: 'own' },
    { resource: 'missions', action: 'create', scope: 'own' },
    { resource: 'missions', action: 'read', scope: 'own' },
    { resource: 'missions', action: 'update', scope: 'own' },
    { resource: 'governance', action: 'read', scope: 'all' },
    { resource: 'resources', action: 'read', scope: 'own' },
    { resource: 'analytics', action: 'read', scope: 'own' },
  ],

  auditor: [
    // Least-privilege compliance observer: audit trail + governance controls +
    // member-scoped missions + read-only overview. NOT operational surfaces
    // (agents/resources) or org config (settings/billing). Kept in lockstep with
    // the authoritative server PermissionSet (auth-service user_handler_permissions).
    { resource: 'dashboard', action: 'read', scope: 'all' },
    { resource: 'organization', action: 'read', scope: 'all' },
    { resource: 'members', action: 'read', scope: 'all' },
    { resource: 'missions', action: 'read', scope: 'own' },
    { resource: 'governance', action: 'read', scope: 'all' },
    { resource: 'analytics', action: 'read', scope: 'all' },
    { resource: 'analytics', action: 'export', scope: 'all' },
    { resource: 'audit', action: 'read', scope: 'all' },
    { resource: 'audit', action: 'export', scope: 'all' },
  ],
};

class PermissionService {
  private currentUser: User | null = null;

  /**
   * Set the current user for permission checks
   */
  setUser(user: User): void {
    this.currentUser = user;
  }

  /**
   * Get the current user
   */
  getUser(): User | null {
    return this.currentUser;
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(
    resource: string,
    action: 'create' | 'read' | 'update' | 'delete' | 'export',
    resourceOwnerId?: string
  ): boolean {
    if (!this.currentUser) return false;

    const userPermissions = rolePermissions[this.currentUser.role];
    const permission = userPermissions.find(
      p => p.resource === resource && p.action === action
    );

    if (!permission) return false;

    // If permission scope is 'all', user can access any resource
    if (permission.scope === 'all') return true;

    // If permission scope is 'own', check ownership
    if (permission.scope === 'own' && resourceOwnerId) {
      return resourceOwnerId === this.currentUser.id;
    }

    // For actions without ownership context, check if permission exists
    return true;
  }

  /**
   * Check if user can edit a specific resource
   */
  canEdit(resource: Resource): boolean {
    if (!this.currentUser) return false;

    // Owners and Admins can edit everything
    if (this.currentUser.role === 'owner' || this.currentUser.role === 'admin') return true;

    // Auditors can't edit anything
    if (this.currentUser.role === 'auditor') return false;

    // Managers can only edit their own resources
    if (this.currentUser.role === 'manager') {
      return resource.createdBy === this.currentUser.id;
    }

    // Users can only edit their own resources
    if (this.currentUser.role === 'member') {
      return resource.createdBy === this.currentUser.id;
    }

    return false;
  }

  /**
   * Check if user can delete a specific resource
   */
  canDelete(resource: Resource): boolean {
    if (!this.currentUser) return false;

    // Owners and Admins can delete everything
    if (this.currentUser.role === 'owner' || this.currentUser.role === 'admin') return true;

    // Auditors can't delete anything
    if (this.currentUser.role === 'auditor') return false;

    // Managers can only delete their own resources
    if (this.currentUser.role === 'manager') {
      return resource.createdBy === this.currentUser.id;
    }

    // Users typically can't delete (depends on resource type)
    if (this.currentUser.role === 'member') {
      return resource.type === 'mission' && resource.createdBy === this.currentUser.id;
    }

    return false;
  }

  /**
   * Get accessible menu items based on user role
   */
  getAccessibleMenuItems(): string[] {
    if (!this.currentUser) return [];

    const menuMap: Record<UserRole, string[]> = {
      owner: [
        'dashboard',
        'organization',
        'agents',
        'users',
        'missions',
        'governance',
        'resources',
        'analytics',
        'settings',
        'billing',
        'audit',
      ],
      admin: [
        'dashboard',
        'organization',
        'agents',
        'users',
        'missions',
        'governance',
        'resources',
        'analytics',
        'settings',
        'billing',
        'audit',
      ],
      manager: [
        'dashboard',
        'organization',
        'agents',
        'users',
        'missions',
        'governance',
        'resources',
        'analytics',
        'settings',
      ],
      member: [
        'dashboard',
        'organization',
        'agents',
        'missions',
        'analytics',
      ],
      auditor: [
        'dashboard',
        'organization',
        'users',
        'missions',
        'governance',
        'analytics',
        'audit',
      ],
    };

    return menuMap[this.currentUser.role] || [];
  }

  /**
   * Check if user has any permission for a resource
   */
  hasAnyPermission(resource: string): boolean {
    if (!this.currentUser) return false;

    const userPermissions = rolePermissions[this.currentUser.role];
    return userPermissions.some(p => p.resource === resource);
  }

  /**
   * Check if user can access a specific page/route
   */
  canAccessRoute(route: string): boolean {
    const routePermissions: Record<string, { resource: string; action: 'read' }> = {
      '/dashboard': { resource: 'dashboard', action: 'read' },
      '/organization': { resource: 'organization', action: 'read' },
      '/agents': { resource: 'agents', action: 'read' },
      '/users': { resource: 'members', action: 'read' },
      '/missions': { resource: 'missions', action: 'read' },
      '/governance': { resource: 'governance', action: 'read' },
      '/resources': { resource: 'resources', action: 'read' },
      '/analytics': { resource: 'analytics', action: 'read' },
      '/settings': { resource: 'settings', action: 'read' },
      '/billing': { resource: 'billing', action: 'read' },
      '/audit': { resource: 'audit', action: 'read' },
    };

    const permission = routePermissions[route];
    if (!permission) return false;

    return this.hasPermission(permission.resource, permission.action);
  }

  /**
   * Get user's role
   */
  getUserRole(): UserRole | null {
    return this.currentUser?.role || null;
  }

  /**
   * Check if user is owner
   */
  isOwner(): boolean {
    return this.currentUser?.role === 'owner';
  }

  /**
   * Check if user is admin (or owner)
   */
  isAdmin(): boolean {
    return this.currentUser?.role === 'owner' || this.currentUser?.role === 'admin';
  }

  /**
   * Check if user is manager or above
   */
  isManagerOrAbove(): boolean {
    return this.currentUser?.role === 'owner' || this.currentUser?.role === 'admin' || this.currentUser?.role === 'manager';
  }

  /**
   * Get permission scope for a resource action
   */
  getPermissionScope(resource: string, action: string): 'own' | 'all' | null {
    if (!this.currentUser) return null;

    const userPermissions = rolePermissions[this.currentUser.role];
    const permission = userPermissions.find(
      p => p.resource === resource && p.action === action
    );

    return permission?.scope || null;
  }
}

// Export singleton instance
export const permissionService = new PermissionService();

// Export hooks for React components
export function usePermission() {
  return {
    hasPermission: permissionService.hasPermission.bind(permissionService),
    canEdit: permissionService.canEdit.bind(permissionService),
    canDelete: permissionService.canDelete.bind(permissionService),
    canAccessRoute: permissionService.canAccessRoute.bind(permissionService),
    getUserRole: permissionService.getUserRole.bind(permissionService),
    isOwner: permissionService.isOwner.bind(permissionService),
    isAdmin: permissionService.isAdmin.bind(permissionService),
    isManagerOrAbove: permissionService.isManagerOrAbove.bind(permissionService),
    getAccessibleMenuItems: permissionService.getAccessibleMenuItems.bind(permissionService),
  };
}